(ns pfo.widget.icons)

;; -----------------------------------------------------------------------------
;; Minimal stroked line icons (Lucide-style: 24x24 grid, stroke="currentColor").
;; Each takes an optional PROPS map merged onto the <svg> (e.g. {:width 18}).
;; -----------------------------------------------------------------------------
(defn- line-icon [props & shapes]
  (into [:svg (merge {:width 18 :height 18 :viewBox "0 0 24 24" :fill "none"
                      :stroke "currentColor" :stroke-width 2
                      :stroke-linecap "round" :stroke-linejoin "round"}
                     props)]
        shapes))

(defn minus
  ([] (minus nil))
  ([props] (line-icon props [:line {:x1 5 :y1 12 :x2 19 :y2 12}])))

(defn plus
  ([] (plus nil))
  ([props] (line-icon props
                       [:line {:x1 12 :y1 5 :x2 12 :y2 19}]
                       [:line {:x1 5 :y1 12 :x2 19 :y2 12}])))

(defn fit-width
  "Double-headed horizontal arrow (move/fit horizontally)."
  ([] (fit-width nil))
  ([props] (line-icon props
                       [:polyline {:points "18 8 22 12 18 16"}]
                       [:polyline {:points "6 8 2 12 6 16"}]
                       [:line {:x1 2 :y1 12 :x2 22 :y2 12}])))

(defn rotate-ccw
  "Counter-clockwise circular arrow (reset / refresh)."
  ([] (rotate-ccw nil))
  ([props] (line-icon props
                       [:polyline {:points "1 4 1 10 7 10"}]
                       [:path {:d "M3.51 15a9 9 0 1 0 2.13-9.36L1 10"}])))

(defn chevron-left
  "Single left chevron (previous)."
  ([] (chevron-left nil))
  ([props] (line-icon props [:polyline {:points "15 18 9 12 15 6"}])))

(defn chevron-right
  "Single right chevron (next)."
  ([] (chevron-right nil))
  ([props] (line-icon props [:polyline {:points "9 18 15 12 9 6"}])))

(defn- thumb-icon
  "Lucide thumb: the cuff line and the hand, painted when FILLED (a submitted rating)."
  [cuff hand props filled]
  (line-icon props
             [:path {:d cuff}]
             [:path {:d hand :fill (if filled "currentColor" "none")}]))

(defn thumbs-up [props filled]
  (thumb-icon "M7 10v12"
              "M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z"
              props filled))

(defn thumbs-down [props filled]
  (thumb-icon "M17 14V2"
              "M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z"
              props filled))

(defn sparkle
  "AARK two-star sparkle. Filled with currentColor so it inherits the surrounding
  text color (teal in the AARK banner, amber in the 'unable to classify' box)."
  ([] (sparkle nil))
  ([props]
   ;; viewBox minY=-3 (not 0): the star paths span y≈1..15, so a plain "0 0 22 22"
   ;; box leaves all the slack at the bottom and the glyph rides high. Shifting
   ;; the box up by 3 balances the top/bottom margin so it centers with the text.
   [:svg (merge {:width 16 :height 16 :viewBox "0 -3 22 22" :fill "none"} props)
    [:path {:d "M9 3L10.2 7.8L15 9L10.2 10.2L9 15L7.8 10.2L3 9L7.8 7.8L9 3Z" :fill "currentColor"}]
    [:path {:d "M17 1L17.8 3.2L20 4L17.8 4.8L17 7L16.2 4.8L14 4L16.2 3.2L17 1Z" :fill "currentColor"}]]))

(defn padlock [props closed? cycle?]

  (if closed?
    [:svg (merge {:viewBox "0 0 500 500"} props)
     [:path {:d "m409.6,197.3h-18v-50.6c0-74.8-60.8-135.7-135.6-135.7s-135.6,60.9-135.6,135.6v50.6h-17.9c-17.7,0-32.1,14.4-32.1,32.1v239.6c0,17.7 14.4,32.1 32.1,32.1h307.2c17.7,0 32.1-14.4 32.1-32.1v-239.6c-0.1-17.6-14.5-32-32.2-32zm-248.4-50.7c0-52.3 42.5-94.8 94.8-94.8 52.3,0 94.8,42.5 94.8,94.8v50.5h-189.6v-50.5zm239.6,313.6h-289.6v-222.1h289.7v222.1z", :id "path3753"}]
     [:path {:d "m256,429.7c11.3,0 20.4-9.1 20.4-20.4v-52.6c0-11.3-9.1-20.4-20.4-20.4s-20.4,9.1-20.4,20.4v52.6c2.84217e-14,11.2 9.1,20.4 20.4,20.4z", :id "path3755"}]
     [:path.c-signature-status__padlock-body 
      {:class (when cycle? "is-cycle"), :d "M 111.39573,349.19409 V 238.4289 H 256 400.60427 V 349.19409 459.95928 H 256 111.39573 Z m 151.02804,79.62575 c 6.815,-2.27712 11.902,-7.82878 13.54018,-14.77697 0.51549,-2.18643 0.62238,-7.44085 0.62754,-30.84724 0.004,-17.02218 -0.16031,-29.11 -0.41364,-30.47619 -0.95024,-5.12464 -5.08381,-11.07242 -9.4894,-13.65427 -6.47736,-3.79598 -14.92642,-3.79459 -21.37407,0.003 -4.39188,2.58713 -8.5494,8.56604 -9.49223,13.65074 -0.25474,1.37381 -0.41032,13.44171 -0.39562,30.68637 0.0217,25.46308 0.0994,28.71012 0.7464,31.18858 1.76434,6.75875 7.12784,12.25572 14.15219,14.50436 2.75946,0.88337 9.05449,0.73824 12.09865,-0.27892 z", :id "path4576", :inkscape:connector-curvature "0"}]]

    [:svg (merge {:viewBox "0 0 500 500"} props)
     [:path {:d "M376,11c-68.9,0-125,60.9-125,135.6v50.6H42.5c-17.4,0-31.5,14.1-31.5,31.5v240.7c0,17.4,14.1,31.5,31.5,31.5h277.8    c17.4,0,31.5-14.1,31.5-31.5V228.8c0-17.4-14.1-31.5-31.5-31.5h-28.5v-50.6c0-52.3,37.8-94.8,84.2-94.8    c46.4,0,84.2,42.5,84.2,94.8v59.3c0,11.3,9.1,20.4,20.4,20.4c11.3,0,20.4-9.1,20.4-20.4v-59.3C501,71.9,444.9,11,376,11z     M311,460.2H51.8V238.1H311V460.2z", :id "path4651"}]
     [:path {:d "m181.4,430.4c11.3,0 20.4-9.1 20.4-20.4v-53.3c0-11.3-9.1-20.4-20.4-20.4-11.3,0-20.4,9.1-20.4,20.4v53.3c0,11.2 9.1,20.4 20.4,20.4z", :id "path4653"}]
     [:path.c-signature-status__padlock-body 
      {:class (when cycle? "is-cycle") 
       , :d "M 52.067797,349.50508 V 239.94576 H 181.15254 310.23729 V 349.50508 459.06441 H 181.15254 52.067797 Z m 139.532453,78.83207 c 9.40143,-5.00722 10.02141,-7.80536 10.09347,-45.55446 l 0.069,-36.14614 -6.67493,-5.61658 c -9.33907,-7.8583 -19.89177,-7.58704 -28.20382,0.72501 l -6.34159,6.34159 v 35.04563 35.04564 l 6.34159,6.34159 c 6.87183,6.87182 16.25947,8.32186 24.71629,3.81772 z", :id "path4667", :inkscape:connector-curvature "0"}]]))
